import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button } from './ui/button';

export function ManualCreateForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (body: {
    card_id: number;
    amount_original: number;
    currency: string;
    occurred_at: string;
    raw_merchant_text: string;
    type: 'purchase' | 'refund' | 'fee' | 'decline';
    department_id?: number;
  }) => void;
  onCancel: () => void;
}) {
  const [cardId, setCardId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('VND');
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 19));
  const [merchant, setMerchant] = useState('');
  const [type, setType] = useState<'purchase' | 'refund' | 'fee' | 'decline'>('purchase');
  const [departmentId, setDepartmentId] = useState<string>('');

  const { data: deptList } = useQuery({
    queryKey: ['departments-lookup'],
    queryFn: () => api.listDepartments(),
    staleTime: 5 * 60_000,
  });

  const inputCls =
    'mt-1 w-full rounded border border-border bg-card p-2 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const padded = occurredAt.length === 16 ? `${occurredAt}:00` : occurredAt;
        onSubmit({
          card_id: parseInt(cardId, 10),
          amount_original: parseFloat(amount),
          currency,
          occurred_at: padded + (padded.endsWith('Z') ? '' : '+07:00'),
          raw_merchant_text: merchant,
          type,
          ...(departmentId ? { department_id: Number(departmentId) } : {}),
        });
      }}
      className="space-y-3 rounded-lg border border-border bg-background p-4 text-sm"
    >
      <fieldset>
        <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
          Transaction details
        </legend>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-muted-foreground">Card ID</span>
            <input
              type="number"
              required
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="text-muted-foreground">Type</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as never)}
              className={inputCls}
            >
              <option value="purchase">Purchase</option>
              <option value="refund">Refund</option>
              <option value="fee">Fee</option>
              <option value="decline">Decline</option>
            </select>
          </label>
          <label className="block">
            <span className="text-muted-foreground">Amount</span>
            <input
              type="number"
              step="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="text-muted-foreground">Currency</span>
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="col-span-2 block">
            <span className="text-muted-foreground">Occurred at (ISO local)</span>
            <input
              type="datetime-local"
              required
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="col-span-2 block">
            <span className="text-muted-foreground">Merchant text</span>
            <input
              required
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="col-span-2 block">
            <span className="text-muted-foreground">Department (optional)</span>
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              className={inputCls}
            >
              <option value="">— Leave unassigned —</option>
              {deptList?.rows.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} — {d.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="default" size="sm">
          Create
        </Button>
      </div>
    </form>
  );
}
